import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Flame } from "lucide-react";
import { whenFree } from "@/lib/popupQueue";
import { confetti, playFanfare } from "@/lib/celebrate";
import { useQuest } from "@/lib/quest";
import { QuestGoals } from "@/components/quest/QuestGoals";
import { useAuth } from "@/context/AuthContext";
import { QuestHeroPopup } from "@/components/quest/QuestHeroPopup";
import { QuestHeroes } from "@/components/quest/QuestHeroBadge";

const KEY = "itz_quest_result";

export function QuestResult() {
  const [q] = useQuest();
  const [open, setOpen] = useState(false);
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fresh = user?.created_at && q?.settled_at && new Date(user.created_at) > new Date(q.settled_at) && !q.was_participant;
  const id = q?.status === "settled" && !q.excluded && !fresh && new Date(q.settled_at) > Date.now() - 3 * 86400000 ? q.id : null;
  useEffect(() => {
    if (!id || localStorage.getItem(`${KEY}_${id}`)) return;
    whenFree(`quest-result-${id}`, () => {
      localStorage.setItem(`${KEY}_${id}`, "1");
      setOpen(true);
      refreshUser?.();
      if (q.success) { playFanfare(); [0, 400, 800].forEach((d, i) => setTimeout(() => confetti({ x: innerWidth * [0.5, 0.2, 0.8][i], y: innerHeight * 0.3, count: 90, spread: 1.8, colors: ["#ff1f4b", "#fff", "#fde68a", "#d2042d"] }), d)); }
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!q) return null;
  if (!open) return <QuestHeroPopup q={q} />;
  const me = q.was_participant;
  const title = q.success ? "Geschafft! 🎉" : "Knapp verpasst 💔";
  const text = q.success
    ? (me ? `Die Community hat alle Ziele erreicht – du bekommst +${q.stake} Punkte!` : `Die Community hat alle Ziele erreicht – ${q.participants} Mitmacher bekommen je +${q.stake} Punkte. Beim nächsten Mal bist du dabei!`)
    : (me ? `Nicht alle Ziele wurden erreicht – dir wurden ${q.stake} Punkte abgezogen. Nächstes Mal schaffen wir das zusammen!` : `Nicht alle Ziele wurden erreicht – die ${q.participants} Mitmacher verlieren je ${q.stake} Punkte.`);
  const close = (go) => { setOpen(false); if (go) navigate("/community-aufgabe"); };
  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm wheel-fade" data-testid="quest-result-popup">
      <div className={`quest-glow sky-celebrate w-full max-w-md rounded-[2rem] p-6 text-white text-center space-y-4 relative overflow-hidden ${q.success ? "" : "grayscale-[.25]"}`}>
        <span className="contest-shine" />
        <p className="relative text-xs font-extrabold uppercase tracking-[0.25em] flex items-center justify-center gap-1.5"><Flame className="w-4 h-4" /> {q.title} · Ergebnis</p>
        <p className="relative font-display text-4xl font-extrabold" data-testid="quest-result-title">{title}</p>
        {me && <p className={`relative font-display text-5xl font-extrabold tabular-nums ${q.success ? "text-amber-200" : "text-white/80"}`} data-testid="quest-result-points">{q.success ? "+" : "−"}{q.stake}</p>}
        <p className="relative text-sm text-red-50" data-testid="quest-result-text">{text}</p>
        <div className="relative text-left"><QuestGoals goals={q.goals} /></div>
        {q.heroes?.length > 0 && <div className="relative text-left space-y-1.5" data-testid="quest-result-heroes"><p className="text-xs font-extrabold uppercase tracking-wider text-amber-200">Aufgaben-Helden</p><QuestHeroes heroes={q.heroes} testId="quest-result-hero-list" /></div>}
        <button onClick={() => close(false)} data-testid="quest-result-ok" className="relative w-full h-12 rounded-full bg-white text-[#b0002a] font-extrabold">OK</button>
        <button onClick={() => close(true)} data-testid="quest-result-details" className="relative w-full text-sm font-semibold text-red-100">Details ansehen</button>
      </div>
    </div>,
    document.body
  );
}
